import './css/App.css';
import './css/Colors.css';

import {BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, Outlet} from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import AuthHandler from './pages/authentication/AuthHandler';
import { DeckPage } from './pages/deck';
import PostPage from './pages/comments/PostPage';

import UserProfile from './pages/UserProfile';
import MultilingdualDeckPage from './pages/decks/MultilingdualDeckPage';
import LanguageCollectionPage from './pages/collections/LanguageCollectionPage';
import EmailSettings from './pages/settings/EmailSettings';
import AccountSettings from './pages/settings/AccountSettings';
import ImportContentPage from './pages/import/ImportContentPage'

import Analytics from './pages/analytics/Analytics';
import Dashboard from './pages/Dashboard';
import EmailVerificationPage from './pages/email/EmailVerificationPage';
import Explore from './pages/explore/Explore';
import CleanDashboard from './pages/dashboard/CleanDashboard';
import ResourcesDirectory from './pages/resources/ResourcesDirectory';
import MainSettings from './pages/settings/MainSettings';
import ProfileSettings from './pages/settings/ProfileSettings';
import DeckSettings from './pages/settings/DeckSettings';
import { DeckSettingsPage as IndividualDeckSettings } from './pages/deck';
import NewNavbar from './components/general/NewNavbar';
import HomepageNavbar from './components/general/HomepageNavbar';

import { 
    MyDecksPage as MyDecks, 
    DeckCardsPage as MyDeckCards, 
    ExportDeckPage as ExportMyDeck,
    ImportDeckPage,
    ImportCsvToDeckPage as ImportCSVToDeck
} from './pages/deck';
import ExportCSV from './pages/deck/importExport/exportCsvPage';
import ExportPDF from './pages/deck/importExport/exportPdfPage';
import MyTables from './pages/tables/mytables/MyTables';
import MyNotes from './pages/notes/mynotes/MyNotes';
import EditMyNote from './pages/notes/mynotes/EditMyNote';
import EditMyText from './pages/texts/EditMyText';
import EditMyCourse from './pages/courses/course/EditMyCourse';
import Play from './pages/play/Play';
import MyTable from './pages/tables/mytables/MyTable';
import MyTablePlay from './pages/tables/mytables/MyTablePlay';
import TableGame from './pages/tables/games/TableGame';
import MyProfile from './pages/MyProfile';
import MyVocabulary from './pages/MyVocabulary';
import MyVocabularySpacedRepetition from './pages/myvocabulary/MyVocabularySpacedRepetition';
import MyVocabularyExport from './pages/MyVocabularyExport';
import WatchYouTube from './pages/videos/WatchYouTube';
import MyDailyGoals from './pages/goals/MyDailyGoals';
import GetStartedPage from './pages/get-started/GetStartedPage';
import CommentFeed from './pages/comments/CommentFeed';
import MyGoals from './pages/goals/MyGoals';
import MyFavorites from './pages/favorites/MyFavorites';
import Login from './pages/authentication/Login';
import ForgottenPassword from './pages/authentication/ForgottenPassword';
import LogOutComponent from './components/authentication/LogOutComponent';
import MyLanguages from './pages/mylanguages/MyLanguages';

import HanziToPinyinConverterPage from './pages/learn/mandarin-chinese/HanziToPinyinConverterPage';
import Translator from './pages/translation/Translator';
import DecksDirectory from './pages/decks/DecksDirectory';
import Premium from './pages/premium/Premium';
import PremiumSuccess from './pages/premium/PremiumSuccess';
import Tools from './pages/tools/Tools';

import PublicDeck from './pages/decks/PublicDeck';
import MyFeedback from './pages/feedback/MyFeedback';
import Onboarding from './pages/onboarding/Onboarding';
import DailyStats from './pages/userStatistics/DailyStats';
import UserProfileDecks from './pages/profile/UserProfileDecks';
import MyStatistics from './pages/userStatistics/MyStatistics';
import MyFlaschardsStatistics from './pages/userStatistics/MyFlaschardsStatistics';
// Removed DeckPlay - games moved to main site
import { AuthProvider } from "./contexts/AuthContext";
import { SocialProvider } from "./contexts/SocialContext";
import { useAuth } from "./contexts/AuthContext";
import { LanguagesProvider } from "./contexts/LanguagesContext";
import { MyContentProvider } from "./contexts/MyContentContext";
import { UserInfoProvider } from "./contexts/UserInfoContext";
import { UserStatisticsProvider } from "./contexts/UserStatisticsContext";
import TextsDirectory from  './pages/texts/TextsDirectory';
import StudyText from './pages/texts/StudyText';
import Forums from './pages/forums/Forums';

import LanguagePage from './pages/learn/LanguagePage';
import AllLanguages from './pages/languages/AllLanguages';
import Roadmap from './pages/Roadmap';
// Removed DeckGame - games moved to main site


import ScrollToTop from "./components/scrollToTop";
import VocabularyItemPage from './pages/myvocabulary/VocabularyItemPage'
import MyLibrary from './pages/mylibrary/MyLibrary';



import MyCalendar from './pages/calendar/MyCalendar';
import FooterMenu from './components/menus/FooterMenu';
import CourseHome from './pages/courses/course/CourseHome';
import CourseLesson from './pages/courses/course/CourseLesson';
import { ExportPdfQuizPage as ExportPDFQuiz } from './pages/deck';
import { ThemeProvider } from './contexts/ThemeContext';


const ADMIN_ALLOWLIST_PATHS = [
  "/admin",
  "/login",
  "/register",
  "/login/forgotten-password",
  "/auth/action",
  "/logout",
];

const isAdminAllowlistedPath = (pathname) => {
  if (pathname === "/login" || pathname.startsWith("/login/")) return true;
  return ADMIN_ALLOWLIST_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
};

// Top-level gate: only admins access the dashboard. Non-admins are redirected
// without touching the main-site (yalango.com) session.
const AdminAccessGuard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { authReady, isAdmin, currentUser } = useAuth();
  const hasAdminSession = Boolean(isAdmin && currentUser);

  useEffect(() => {
    if (!authReady) return;
    if (isAdminAllowlistedPath(location.pathname)) return;
    if (hasAdminSession) return;

    if (process.env.NODE_ENV === "development") {
      navigate("/admin", { replace: true });
    } else {
      window.location.href = "https://yalango.com";
    }
  }, [authReady, hasAdminSession, location.pathname, navigate]);

  if (!authReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-persian-green mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAdminAllowlistedPath(location.pathname)) {
    return <Outlet />;
  }

  if (!hasAdminSession) {
    return null;
  }

  return <Outlet />;
};

// Authenticated admin routes (AdminAccessGuard already ensures isAdmin)
const PrivateRouteWrapper = () => {
  const { currentUser } = useAuth();

  if (currentUser === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-persian-green mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (currentUser === null) {
    return null;
  }

  return <Outlet />;
};

const DashboardLayout = () => {
  const [navbarCollapsed, setNavbarCollapsed] = useState(false);
  //<WidgetMenu />
  return (
    <>
      <NewNavbar navbarCollapsed={navbarCollapsed} setNavbarCollapsed={setNavbarCollapsed} />
      
      <div className={`${navbarCollapsed ? "dashboard-collapsed" : "dashboard"} `}>
        <Outlet />
      </div>
      <div className=" ">
        </div>
    </>
  )
}


const HomepageLayout = () => {
  return (
    <>
      <div className="relative homepage flex flex-col ">
        <HomepageNavbar />
        <div className=" flex-grow px-8 py-8 pt-12 lg:pt-0 lg:mt-7">
          <Outlet />
        </div>
        <FooterMenu />
        <div className="dotted-background ">
        </div>
      </div>
    </>
  )
}

function App() {

  return (
    <>
      <Router>
        <ThemeProvider>
          <div className="gray-background-gradient dark:dark-mode-background-gradient">
            <ScrollToTop />
            <AuthProvider>
            <LanguagesProvider>
              <UserInfoProvider>
                <UserStatisticsProvider>
                
                <MyContentProvider>
                  <SocialProvider>
              <Routes>
                <Route element={<AdminAccessGuard />}>

                {/* Allowlisted auth paths (reachable without admin) */}
                <Route path="/admin" element={<Login />} />
                <Route path="/login" element={<Navigate to="/admin" replace />} />
                <Route path="/register" element={<Navigate to="/admin" replace />} />
                <Route path="/login/forgotten-password" element={<ForgottenPassword />} />
                <Route path="/logout" element={<LogOutComponent />} />
                <Route path="/auth/action" element={<AuthHandler />} />

                {/* Removed vocabulary game routes - games moved to main site */}

                {/* Removed deck language game routes - games moved to main site */}

                {/* Removed individual deck game routes - games moved to main site */}

                {/* Removed review and category game routes - games moved to main site */}
                
                {/* Removed folder game routes - games moved to main site */}
                
                <Route path="/my-tables/:id/fill-in" element={<TableGame game="fill-in" />} />   
                <Route path="/my-tables/:id/flashcards" element={<TableGame game="flashcards" />} />   

                {/* Removed more deck game routes - games moved to main site */}
                
                
                {/* Removed public deck game routes - games moved to main site */}

                <Route path="/course/:courseId/lesson/:lessonId" element={<CourseLesson />} /> 

                {/* Removed remaining deck game routes - games moved to main site */}
                <Route path="/texts/:targetLang/:text_id/:slug" element={<StudyText location={"public"} />} />
                <Route path="/my-texts/:text_id/study" element={<StudyText location={"library"} />} />

                {/* Onboarding route - outside DashboardLayout to avoid sidebar */}
                <Route element={<PrivateRouteWrapper />}>
                  <Route path="/onboarding" element={<Onboarding />} />
                  <Route path="/email-verification" element={<EmailVerificationPage />} />
                </Route>

                <Route element={<DashboardLayout />}>
                  <Route path="/my-texts/:id" element={<EditMyText />} />
                  <Route path="/my-courses/:courseId" element={<EditMyCourse />} />
                  
                  
                  <Route path="/my-vocabulary/:targetLang/item/:id" element={<VocabularyItemPage />} />
                  
                  <Route path="/analytics" element={<Analytics />} />

                  
                  <Route element={<PrivateRouteWrapper />}>
                    
                    <Route path="/current-plan" element={<Premium />} />
                    <Route path="/import" element={<ImportContentPage />} />
                    <Route path="/import/decks/csv" element={<ImportCSVToDeck design="page" mode="create-deck" type={"csv"} />} />
                    <Route path="/import/decks/quizlet" element={<ImportCSVToDeck design="page" mode="create-deck"  type={"quizlet"} />} />
                    <Route path="/import/decks/table" element={<ImportCSVToDeck design="page" mode="create-deck" type={"table"} />} />
                    <Route path="/import/decks/input-text" element={<ImportCSVToDeck design="page" mode="create-deck"  type={"inputText"} />} />

                    <Route path="/my-calendar" element={<MyCalendar />} />
                    <Route path ="/watch" element={<WatchYouTube />} />
                    <Route path="/get-started" element={<GetStartedPage />} />
                    <Route path="/current-plan/success" element={<PremiumSuccess />} />
                    {/* Removed DeckPlay route - games moved to main site */}
                    {/* Removed more DeckPlay routes - games moved to main site */}
                    <Route path="/my-decks/cards" element={<MyDeckCards />} />
                    <Route path="/my-decks" element={<MyDecks />} />
                    <Route path="/my-decks/:id" element={<DeckPage />} />

                    <Route path="/my-multilingual-decks/:id" element={<MultilingdualDeckPage />} />
                    <Route path="/my-language-collections/:docId" element={<LanguageCollectionPage />} />

                    {/* Removed folder play routes - games moved to main site */}
                    <Route path="/my-decks/:id/export" element={<ExportMyDeck />} />
                    <Route path="/my-decks/:id/export/csv" element={<ExportCSV />} />
                    <Route path="/my-decks/:id/export/pdf" element={<ExportPDF />} />
                    <Route path="/my-decks/:id/export/quiz" element={<ExportPDFQuiz />} />
                    
                    <Route path="/my-decks/:id/import" element={<ImportDeckPage />} />
                    <Route path="/my-decks/:id/settings" element={<IndividualDeckSettings />} />
                    <Route path="/my-decks/:id/import/csv" element={<ImportCSVToDeck design="page" mode="edit-deck" type={"csv"} />} />
                    <Route path="/my-decks/:id/import/quizlet" element={<ImportCSVToDeck design="page" mode="edit-deck"  type={"quizlet"} />} />
                    <Route path="/my-decks/:id/import/table" element={<ImportCSVToDeck design="page" mode="edit-deck" type={"table"} />} />
                    <Route path="/my-decks/:id/import/input-text" element={<ImportCSVToDeck design="page" mode="edit-deck"  type={"inputText"} />} />
                    
                    <Route path="/tools/translator" element={<Translator />} />
                    <Route path="/my-library" element={<MyLibrary libraryPage={"library"}  />} />
                    <Route path="/my-library/folder/:folderDocId" element={<MyLibrary libraryPage={"folder"} />} />
                    <Route path ="/my-favorites" element={<MyFavorites />} />
                    
                    <Route path="/my-tables" element={<MyTables />} />
                    <Route path="/my-goals/daily" element={<MyDailyGoals />} />
                    <Route path ="/my-goals" element={<MyGoals />} />
                    <Route path="/my-tables/:id" element={<MyTable />} />   
                    <Route path="/my-tables/:id/play" element={<MyTablePlay />} />      
                    <Route path="/my-notes" element={<MyNotes />} />
                    <Route path="/my-notes/:id" element={<EditMyNote />} /> 
                    <Route path="/stats/daily" element={<DailyStats />} />

                    
                    <Route path="/my-feedback" element={<MyFeedback />} />
                    <Route path="/my-statistics" element={<MyStatistics />} />
                    <Route path="/my-statistics/flashcards" element={<MyFlaschardsStatistics />} />
                    <Route path="/roadmap" element={<Roadmap />} />
                    <Route path="/my-profile" element={<MyProfile />} />
                    <Route path="/tools" element={<Tools />} />
                    <Route path="/my-vocabulary" element={<MyVocabulary />} />
                    <Route path="/my-vocabulary/spaced-repetition" element={<MyVocabularySpacedRepetition />} />
                    <Route path="/my-vocabulary/export" element={<MyVocabularyExport />} />
                    
                    <Route path="/dashboard" element={<CleanDashboard />} />
                    <Route path="/play" element={<Play />} />
                    <Route path="/old-dashboard" element={<Dashboard />} />
                    <Route path="/settings" element={<MainSettings />} />
                    <Route path="/settings/profile" element={<ProfileSettings />} />
                    <Route path="/settings/email" element={<EmailSettings />} />
                    <Route path="/settings/decks" element={<DeckSettings />} />
                    <Route path="/settings/account" element={<AccountSettings />} />
                    <Route path="/my-languages" element={<MyLanguages />} />  
                    <Route path="/" element={<CleanDashboard />} /> 
                  </Route>
              
                </Route>
                <Route element={<DashboardLayout />}>
                  <Route path="/languages" element={<AllLanguages />} />
                  <Route path="/learn/:language" element={<LanguagePage />} />
                  <Route path="/course/:courseId" element={<CourseHome />} /> 

                  <Route path="/learn/Mandarin-Chinese/hanzi-to-pinyin" element={<HanziToPinyinConverterPage />} />
                  {/* Removed public deck play route - games moved to main site */}
                  <Route path={"/post/:commentDocId"} element={<PostPage />} />
                  {/* Removed section and category play routes - games moved to main site */}
                  <Route path="/explore" element={<Explore />} />
                  <Route path="/decks" element={<DecksDirectory />} />
                  <Route path="/decks/:id" element={<PublicDeck />} />
                  <Route path="/resources" element={<ResourcesDirectory />} />
                  <Route path="/u/:username" element={<UserProfile />} />
                  <Route path="/u/:username/decks" element={<UserProfileDecks />} />
                  <Route path="/forums" element={<Forums />} />
                  <Route path="/texts" element={<TextsDirectory />} />
                  <Route path="/feed" element={<CommentFeed />} />
                  <Route path="/forums/:language" element={<CommentFeed />} />
                </Route>
                </Route>
              </Routes>
              
              </SocialProvider>
              </MyContentProvider>
              </UserStatisticsProvider>
              </UserInfoProvider>
                  </LanguagesProvider>
            </AuthProvider>
          </div>
        </ThemeProvider>
      </Router>
    </>
  );
}

export default App;
